import { useContext, useMemo } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Button, Icon, IconButton, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AttributesType,
  HydratedAttributeGroupsItem,
} from '@atom/types/inventory';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AttributeDetailsContext, {
  AttributeView,
} from './AttributeDetailsContext';

import classes from './attributeDetails.module.css';

interface Props {
  attributeGroup: HydratedAttributeGroupsItem;
}

const AttributeGroupHeader = ({ attributeGroup }: Props) => {
  const {
    editGroup,
    setEditGroup,
    toggleExpanded,
    expanded,
    loading,
    onCancel,
    onSubmit,
    canUpdate,
    errorAttributes,
    view,
  } = useContext(AttributeDetailsContext);

  const handleEditClick = () => {
    if (!expanded.has(attributeGroup.id)) {
      toggleExpanded(attributeGroup.id);
    }

    setEditGroup(attributeGroup);
  };

  const hasAttributeError = !isNilOrEmpty(
    R.pathOr([], [attributeGroup.id], errorAttributes),
  );
  const attributeGroupLoading = loading.includes(attributeGroup.name);
  const isEditMode = editGroup?.id === attributeGroup?.id;
  const isDisabled = !isNilOrEmpty(editGroup) && !isEditMode;

  const getControls = () => {
    const showSaveButton = view !== AttributeView.SCHEMA_PREVIEW;

    return isEditMode ? (
      <>
        {attributeGroupLoading && (
          <div className={classes.loadingContainer}>
            <Progress size={25} thickness={2} />
          </div>
        )}
        <Button onClick={onCancel} disabled={attributeGroupLoading}>
          CANCEL
        </Button>
        {showSaveButton && (
          <Button
            color="primary"
            disabled={attributeGroupLoading}
            onClick={onSubmit}
          >
            SAVE
          </Button>
        )}
      </>
    ) : (
      <>
        {canUpdate && (
          <IconButton disabled={isDisabled} onClick={handleEditClick}>
            <Icon color={isDisabled ? colors.neutral.silver : 'inherit'}>
              edit
            </Icon>
          </IconButton>
        )}
        <IconButton onClick={() => toggleExpanded(attributeGroup?.id)}>
          <Icon>
            {expanded.has(attributeGroup?.id)
              ? 'keyboard_arrow_up'
              : 'keyboard_arrow_down'}
          </Icon>
        </IconButton>
      </>
    );
  };

  const hasPendingChanges = useMemo(() => {
    return R.values(attributeGroup?.attributes).some(
      (attribute: AttributesType) => !isNilOrEmpty(attribute.changeId),
    );
  }, [attributeGroup]);

  return (
    <div className={classes.attributeGroupHeader}>
      <div className={classes.titleContainer}>
        <div
          className={clsx(
            classes.attributeGroupTitle,
            hasAttributeError && classes.error,
          )}
        >
          {attributeGroup.name}
        </div>
        {hasPendingChanges && <div className={classes.changeIndicator} />}
      </div>
      <div className={classes.attributeGroupControls}>{getControls()}</div>
    </div>
  );
};

export default AttributeGroupHeader;
